import { MAX_TOOLTIP_LENGTH, TEXT_ALIGNS, effectiveScale, hasOptions, isChoice, pinnedQuestionIds, questionTypeLabel, type Definition, type Issue, type Question, type Validation } from '@survey/core';
import { ArrowDown, ArrowUp, BookmarkPlus, Copy, CornerDownRight, Eye, GitBranch, ListPlus, MoreHorizontal, Shuffle, Trash2, Workflow } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import { Field, Menu, Switch } from '../ui';
import { LogicEditor } from './LogicEditor';
import { MediaEditor } from './MediaEditor';
import { TypePicker } from './TypePicker';
import { CarryForwardPicker, ConstantSumSettings, OptionList, PipeMenu, ScaleLabels, SliderSettings } from './TypeEditors';
import { conditionPhrase } from './logicText';
import { changeType, logicFrom, randomizerOfSection, TYPE_ICONS } from './model';

interface Props {
  def: Definition;
  question: Question;
  index: number;
  issues: Issue[];
  onChange: (q: Question) => void;
  onMove: (delta: number) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onShowFlow?: () => void;
  /** Jumps to another question (used by the logic links). */
  onSelect?: (id: string) => void;
  /** The question's text, code, type, help text, image or video and "required" are edited on its card (Preview mode), so the panel leaves them out. */
  inlineBasics?: boolean;
  /** Saves a copy of the question to the shared question library. */
  onSaveToLibrary?: () => void;
}

function numOrUndef(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function QuestionEditor({ def, question: q, index, issues, onChange, onMove, onDuplicate, onDelete, onShowFlow, onSelect, inlineBasics, onSaveToLibrary }: Props) {
  const { t, locale } = useI18n();
  const hasScores = !!(q.options ?? []).some((o) => typeof o.score === 'number');
  const [scoring, setScoringState] = useState(hasScores);
  useEffect(() => setScoringState(hasScores), [q.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setScoring = (on: boolean) => {
    setScoringState(on);
    if (!on && hasScores) onChange({ ...q, options: q.options?.map(({ score: _score, ...o }) => o) });
  };
  const titleRef = useRef<HTMLInputElement>(null);
  const insertIntoTitle = (token: string) => {
    const el = titleRef.current;
    const at = el?.selectionStart ?? q.title.length;
    onChange({ ...q, title: q.title.slice(0, at) + token + q.title.slice(el?.selectionEnd ?? at) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };
  const Icon = TYPE_ICONS[q.type];
  const v = q.validation ?? {};
  const setValidation = (patch: Partial<Validation>) => {
    const next = { ...v, ...patch };
    for (const k of Object.keys(next) as (keyof Validation)[]) if (next[k] === undefined || next[k] === false) delete next[k];
    onChange({ ...q, validation: Object.keys(next).length ? next : undefined });
  };
  // Linked by display logic, carry-forward or a jump: such questions stay in place unless the person lets them shuffle.
  const linkedByLogic = pinnedQuestionIds({ ...def, questions: def.questions.map((x) => ({ ...x, fixedPosition: false, shuffleWithLogic: false })) }).has(q.id);
  const pinnedByLogic = linkedByLogic && !q.shuffleWithLogic;
  const leads = logicFrom(def, q.id);
  const numberOf = (id: string) => def.questions.findIndex((x) => x.id === id) + 1;
  const randomizer = randomizerOfSection(def, q.sectionTitle?.trim() ?? '');
  const shuffling = !!def.settings.randomizeQuestions || !!def.settings.randomizeSections;

  return (
    <div className="card qe">
      <div className="card-head qe-head">
        <div className="tile">
          <Icon />
        </div>
        <div className="grow qe-title">
          <h2 title={t('qe.question_n', { n: index + 1 })}>{t('qe.question_n', { n: index + 1 })}</h2>
          <div className="sub">{questionTypeLabel(q.type, locale)}</div>
        </div>
        <div className="qe-tools">
          <button className="btn ghost sm icon" onClick={() => onMove(-1)} disabled={index === 0} aria-label={t('qe.move_up')} title={t('qe.move_up')}>
            <ArrowUp />
          </button>
          <button className="btn ghost sm icon" onClick={() => onMove(1)} disabled={index === def.questions.length - 1} aria-label={t('qe.move_down')} title={t('qe.move_down')}>
            <ArrowDown />
          </button>
          <Menu
            align="right"
            trigger={(toggleMenu) => (
              <button type="button" className="btn ghost sm icon" onClick={toggleMenu} aria-label={t('flow.more_actions')} title={t('flow.more_actions')}>
                <MoreHorizontal />
              </button>
            )}
          >
            {(close) => (
              <>
                <button type="button" onClick={() => { close(); onDuplicate(); }}>
                  <Copy /> {t('qe.duplicate')}
                </button>
                {onSaveToLibrary && (
                  <button type="button" onClick={() => { close(); onSaveToLibrary(); }}>
                    <BookmarkPlus /> {t('library.save')}
                  </button>
                )}
                <button type="button" className="danger" onClick={() => { close(); onDelete(); }}>
                  <Trash2 /> {t('qe.delete')}
                </button>
              </>
            )}
          </Menu>
        </div>
      </div>

      <div className="card-pad form-grid" style={{ paddingTop: 0 }}>
        {issues.length > 0 && (
          <div className="banner warn">
            <div>
              {issues.map((i, n) => (
                <div key={n}>{i.message}</div>
              ))}
            </div>
          </div>
        )}

        {!inlineBasics && (
          <>
        <div className="grid qe-top">
          <Field label={t(q.type === 'descriptive' ? 'qe.text' : 'qe.question')} htmlFor="q-title">
            <div className="row gap-8">
              <input ref={titleRef} id="q-title" className="input" value={q.title} placeholder={t('qe.question_placeholder')} maxLength={500} onChange={(e) => onChange({ ...q, title: e.target.value, titleRich: undefined })} />
              <PipeMenu def={def} question={q} onInsert={insertIntoTitle} />
            </div>
          </Field>
        <Field label={t('qe.question_code')} htmlFor="q-code" hint={t('qe.question_code_hint')}>
          <input id="q-code" className="input" value={q.questionCode ?? ''} maxLength={32} placeholder={t('qe.question_code_placeholder')} onChange={(e) => onChange({ ...q, questionCode: e.target.value || undefined })} />
        </Field>
          <Field label={t('qe.type')} htmlFor="q-type">
            <TypePicker id="q-type" value={q.type} onChange={(type) => onChange(changeType(q, type))} />
          </Field>
        </div>
        <Field label={t('qe.help_text')} htmlFor="q-desc">
          <input id="q-desc" className="input" value={q.description ?? ''} placeholder={t('qe.help_placeholder')} onChange={(e) => onChange({ ...q, description: e.target.value || undefined, descriptionRich: undefined })} />
        </Field>
            {(q.titleRich || q.descriptionRich) && <p className="hint">{t('qe.rich_hint')}</p>}
            <div className="field">
              <span className="label">{t('qe.text_align')}</span>
              <div className="seg" role="radiogroup" aria-label={t('qe.text_align')}>
                {TEXT_ALIGNS.map((a) => (
                  <button key={a} type="button" role="radio" aria-checked={(q.textAlign ?? 'left') === a} className={(q.textAlign ?? 'left') === a ? 'active' : ''} onClick={() => onChange({ ...q, textAlign: a === 'left' ? undefined : a })}>
                    {t(`rich.align_${a}`)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        <Field
          label={t('qe.tooltip')}
          htmlFor="q-tip"
          hint={t('qe.tooltip_hint', { used: [...(q.tooltip ?? '')].length, max: MAX_TOOLTIP_LENGTH })}
        >
          <textarea
            id="q-tip"
            className="textarea"
            style={{ minHeight: 60 }}
            value={q.tooltip ?? ''}
            maxLength={MAX_TOOLTIP_LENGTH}
            placeholder={t('qe.tooltip_placeholder')}
            onChange={(e) => onChange({ ...q, tooltip: e.target.value || undefined })}
          />
        </Field>
        {!inlineBasics && (
          <div className="field">
            <span className="label">{t('media.title')}</span>
            <MediaEditor media={q.media} onChange={(media) => onChange({ ...q, media })} />
          </div>
        )}
        {!inlineBasics && q.type !== 'descriptive' && <Switch checked={q.required} onChange={(required) => onChange({ ...q, required })} label={t('qe.required')} />}

        {(isChoice(q.type) || q.type === 'ranking') && <CarryForwardPicker def={def} question={q} onChange={onChange} />}

        {hasOptions(q.type) && !q.carryForward && !(inlineBasics && isChoice(q.type)) && (
          <div className="field">
            <div className="row gap-8">
              <span className="label grow">{t(q.type === 'matrix' ? 'qe.columns' : q.type === 'constant_sum' ? 'qe.items' : q.type === 'text_form' ? 'qe.fields' : 'qe.options')}</span>
              {(isChoice(q.type) || q.type === 'matrix') && <Switch checked={scoring} onChange={setScoring} label={t('qe.assign_points')} />}
            </div>
            <OptionList question={q} kind={q.type === 'matrix' ? 'columns' : q.type === 'constant_sum' ? 'items' : q.type === 'text_form' ? 'fields' : 'choice'} onChange={onChange} scoring={scoring} />
          </div>
        )}

        {q.type === 'matrix' && (
          <>
            <div className="field">
              <span className="label">{t('qe.rows')}</span>
              <OptionList question={q} kind="rows" onChange={onChange} />
            </div>
            <Switch checked={!!q.matrixMultiple} onChange={(m) => onChange({ ...q, matrixMultiple: m || undefined })} label={t('qe.matrix_multiple')} />
          </>
        )}

        {q.type === 'rating' && (
          <div className="grid cols-2">
            <Field label={t('qe.scale')}>
              <div className="row gap-8">
                <select className="select" value={effectiveScale(q).min} aria-label={t('qe.scale_min')} onChange={(e) => onChange({ ...q, scale: { ...effectiveScale(q), min: Number(e.target.value) } })}>
                  {[0, 1].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
                <span className="muted">{t('qe.to')}</span>
                <select className="select" value={effectiveScale(q).max} aria-label={t('qe.scale_max')} onChange={(e) => onChange({ ...q, scale: { ...effectiveScale(q), max: Number(e.target.value) } })}>
                  {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>
            </Field>
            <div className="grid cols-2">
              <ScaleLabels question={q} onChange={onChange} />
            </div>
          </div>
        )}

        {q.type === 'nps' && (
          <div className="grid cols-2">
            <ScaleLabels question={q} onChange={onChange} />
          </div>
        )}

        {q.type === 'slider' && <SliderSettings question={q} onChange={onChange} />}

        {q.type === 'constant_sum' && <ConstantSumSettings question={q} onChange={onChange} />}

        {q.type === 'text_form' && (
          <Field label={t('qe.max_length')}>
            <input className="input" type="number" min={1} style={{ maxWidth: 200 }} value={v.maxLength ?? ''} onChange={(e) => setValidation({ maxLength: numOrUndef(e.target.value) })} />
          </Field>
        )}

        {(q.type === 'file_upload' || q.type === 'signature' || q.type === 'location' || q.type === 'descriptive') && <p className="hint">{t(`qe.about_${q.type}`)}</p>}

        {q.type === 'number' && (
          <div className="grid cols-3">
            <Field label={t('qe.minimum')}>
              <input className="input" type="number" value={v.min ?? ''} onChange={(e) => setValidation({ min: numOrUndef(e.target.value) })} />
            </Field>
            <Field label={t('qe.maximum')}>
              <input className="input" type="number" value={v.max ?? ''} onChange={(e) => setValidation({ max: numOrUndef(e.target.value) })} />
            </Field>
            <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
              <Switch checked={!!v.integer} onChange={(integer) => setValidation({ integer })} label={t('qe.whole_numbers')} />
            </div>
          </div>
        )}

        {(q.type === 'short_text' || q.type === 'long_text') && (
          <div className="grid cols-3">
            <Field label={t('qe.min_length')}>
              <input className="input" type="number" min={0} value={v.minLength ?? ''} onChange={(e) => setValidation({ minLength: numOrUndef(e.target.value) })} />
            </Field>
            <Field label={t('qe.max_length')}>
              <input className="input" type="number" min={1} value={v.maxLength ?? ''} onChange={(e) => setValidation({ maxLength: numOrUndef(e.target.value) })} />
            </Field>
            <Field label={t('qe.placeholder')}>
              <input className="input" value={q.placeholder ?? ''} onChange={(e) => onChange({ ...q, placeholder: e.target.value || undefined })} />
            </Field>
          </div>
        )}

        {q.type === 'multiple_choice' && (
          <div className="grid cols-2">
            <Field label={t('qe.min_selections')}>
              <input className="input" type="number" min={0} value={v.minSelections ?? ''} onChange={(e) => setValidation({ minSelections: numOrUndef(e.target.value) })} />
            </Field>
            <Field label={t('qe.max_selections')}>
              <input className="input" type="number" min={1} value={v.maxSelections ?? ''} onChange={(e) => setValidation({ maxSelections: numOrUndef(e.target.value) })} />
            </Field>
          </div>
        )}
      </div>

      <div className="divider" />
      <div className="card-head">
        <div className="tile">
          <GitBranch />
        </div>
        <div className="grow">
          <h2>{t('qe.display_logic')}</h2>
          <div className="sub">{t('qe.display_logic_sub')}</div>
        </div>
        {onShowFlow && (
          <button type="button" className="btn sm" onClick={onShowFlow}>
            <Workflow /> {t('builder.view_flow')}
          </button>
        )}
      </div>
      <div className="card-pad stack gap-12 logic-panel" style={{ paddingTop: 0, minWidth: 0 }}>
        <LogicEditor def={def} question={q} onChange={onChange} />
        {leads.length > 0 && (
          <div className="logic-leads">
            <div className="logic-leads-head">
              <strong>{t('qe.logic_leads')}</strong>
              <span className="muted">{t('qe.logic_leads_sub')}</span>
            </div>
            <ul>
              {leads.map((l, i) => {
                const target = t('flow.q_ref', { n: numberOf(l.target.id) });
                const cond = l.conditions.map((c) => conditionPhrase(q, c.operator, c.value, t, locale)).join(` ${t(l.match === 'any' ? 'flow.or' : 'flow.and')} `);
                return (
                  <li key={`${l.kind}-${l.target.id}-${i}`}>
                    {l.kind === 'shows' ? <Eye /> : <ListPlus />}
                    <span className="grow">
                      {l.kind === 'shows' ? (
                        <>
                          {t('qe.logic_shows', { cond })} <strong>{target}</strong>
                        </>
                      ) : (
                        <>
                          {t('qe.logic_carries')} <strong>{target}</strong>
                        </>
                      )}
                      <span className="muted"> · {l.target.title || t('common.untitled_question')}</span>
                    </span>
                    {onSelect && (
                      <button type="button" className="btn ghost sm icon" onClick={() => onSelect(l.target.id)} aria-label={t('qe.logic_go', { q: target })} title={t('qe.logic_go', { q: target })}>
                        <CornerDownRight />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="divider" />
      <div className="card-head">
        <div className="tile">
          <Shuffle />
        </div>
        <div className="grow">
          <h2>{t('qe.randomization')}</h2>
          <div className="sub">{t(shuffling ? 'qe.randomization_sub' : 'qe.randomization_sub_off')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-8" style={{ paddingTop: 0 }}>
        {!shuffling && !randomizer && <p className="hint">{t('qe.shuffle_off_hint')}</p>}
        {randomizer && (
          <div className="banner">
            <Shuffle />
            <span className="grow">{t('qe.randomizer_member', { present: randomizer.present, total: randomizer.sections.length })}</span>
            {onShowFlow && (
              <button type="button" className="btn sm" onClick={onShowFlow}>
                <Workflow /> {t('qe.edit_in_flow')}
              </button>
            )}
          </div>
        )}
        <Switch
          checked={!!q.fixedPosition || pinnedByLogic}
          disabled={pinnedByLogic}
          onChange={(fixedPosition) => onChange({ ...q, fixedPosition: fixedPosition || undefined })}
          label={t('qe.keep_position')}
        />
        {shuffling && !pinnedByLogic && <p className="hint">{t(q.fixedPosition ? 'qe.keep_position_on' : 'qe.keep_position_off')}</p>}
        {linkedByLogic && (
          <>
            <Switch checked={!!q.shuffleWithLogic} onChange={(on) => onChange({ ...q, shuffleWithLogic: on || undefined, fixedPosition: on ? undefined : q.fixedPosition })} label={t('qe.shuffle_with_logic')} />
            <p className="hint">{t(q.shuffleWithLogic ? 'qe.shuffle_with_logic_hint' : 'qe.pinned_by_logic')}</p>
          </>
        )}
      </div>
    </div>
  );
}
